import { createFileRoute, Link, useNavigate, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { Eye, EyeOff, Building2, UserCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { getStoredAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type LoginSearch = { redirect?: string };

/* Where a user lands after authenticating.
   - Roled users (super admin / business owners / moderators) go to their admin
     dashboard, never to a personal account/profile page (avoids landing on
     Profile after a session-expiry redirect).
   - Guests honour their intended destination, else their dashboard. */
function resolveLoginDest(redirect: unknown, roled: boolean): string {
  const rd = typeof redirect === "string" && redirect.startsWith("/") ? redirect : "";
  if (roled) {
    const isAccountPage = /\/(profile|wallet|my-bookings|bookings)(\/|$)/.test(rd);
    return rd.startsWith("/admin/") && !isAccountPage ? rd : "/admin/dashboard";
  }
  return rd || "/dashboard";
}

export const Route = createFileRoute("/_auth/login")({
  validateSearch: (s: Record<string, unknown>): LoginSearch => ({
    redirect: typeof s.redirect === "string" ? s.redirect : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (typeof window === "undefined") return;
    const auth = getStoredAuth();
    if (auth) {
      throw redirect({ to: resolveLoginDest(search.redirect, !!auth.role) as any });
    }
  },
  component: LoginPage,
});

const SUPER_ADMIN_EMAIL = "oyohousing@gmail.com";

function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const isSuperAdmin = email.toLowerCase() === SUPER_ADMIN_EMAIL;
  const isVenueManager = email.toLowerCase().startsWith("manager") || email.toLowerCase().startsWith("host");

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) return toast.error("Please enter your password");
    setLoading(true);
    try {
      const user = await login(email, password);
      const roleLabel = user.role ? ({
        super_admin: "Super Admin",
        venue_manager: "Venue Manager",
        hotel_owner: "Hotel Owner",
        event_centre_owner: "Event Centre Owner",
        open_space_owner: "Open Space Owner",
        resort_owner: "Resort Owner",
        hotel_event_owner: "Hotel & Event Owner",
        moderator: "Moderator",
      } as Record<string, string>)[user.role] : null;
      if (roleLabel) {
        toast.success(`Welcome back, ${roleLabel}`);
      } else {
        toast.success("Welcome back!");
      }
      // Navigate immediately — don't wait for useEffect (prevents form flash)
      const dest = resolveLoginDest(search.redirect, !!user.role);
      navigate({ to: dest as any, replace: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message.replace(/^HTTP \d+:\s*/i, "") : "Sign-in failed. Please try again.";
      toast.error(msg);
      setLoading(false);
    }
  };

  const inputCls = "h-12 rounded-xl border-border/80 bg-background px-4 text-base font-medium placeholder:text-muted-foreground/50 focus-visible:ring-2 focus-visible:ring-primary/30";

  return (
    <section className="mx-auto flex min-h-[calc(100vh-80px)] max-w-md flex-col justify-center px-4 py-8">
      <div className="animate-fade-up w-full rounded-3xl border border-border/60 bg-card p-8 sm:p-10 shadow-elegant">

        {/* Logo / Brand */}
        <div className="animate-fade-in mb-7 text-center">
          <Link to="/"><span className="font-display text-2xl font-bold text-primary">OyoBooking</span></Link>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight">Welcome back</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Enter your credentials to continue.</p>
        </div>

        {/* Role indicator pill */}
        {(isSuperAdmin || isVenueManager) && (
          <div className={`animate-pop mb-5 flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${
            isSuperAdmin
              ? "bg-primary/10 text-primary border border-primary/20"
              : "bg-blue-50 text-blue-700 border border-blue-200"
          }`}>
            {isSuperAdmin ? <Building2 className="size-4 shrink-0" /> : <UserCircle2 className="size-4 shrink-0" />}
            {isSuperAdmin ? "Signing in as Super Admin" : "Signing in as Venue Manager"}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="animate-fade-in delay-150 space-y-1.5">
            <Label htmlFor="email" className="text-sm font-bold">Email address</Label>
            <Input
              id="email" type="email" required autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputCls}
            />
          </div>

          <div className="animate-fade-in delay-225 space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-sm font-bold">Password</Label>
              <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <Input
                id="password" type={showPw ? "text" : "password"} required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className={`${inputCls} pr-12`}
              />
              <button type="button" onClick={() => setShowPw(s => !s)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                {showPw ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
          </div>

          <div className="animate-fade-in delay-300 pt-1">
            <Button type="submit" disabled={loading} size="lg" className="h-12 w-full rounded-xl text-base font-bold">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </div>
        </form>

        <div className="animate-fade-in delay-375 mt-6">
          <div className="relative flex items-center">
            <div className="flex-1 border-t border-border" />
            <span className="mx-3 text-xs text-muted-foreground">or</span>
            <div className="flex-1 border-t border-border" />
          </div>
          <p className="mt-3 text-center text-sm text-muted-foreground">
            New to OyoBooking?{" "}
            <Link to="/signup" className="font-semibold text-primary hover:underline">Create an account</Link>
          </p>
        </div>
      </div>
    </section>
  );
}
